import React, { useEffect, useState, useRef } from "react";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Tooltip } from "reactstrap";
import PropTypes from "prop-types";
import cx from "classnames";

import { Icon } from "./icon";
import { Input } from "./input";
import { getRandomString } from "../../_helpers/utils";

import global from "../../scss/dhp.scss";

let style = Object.assign({}, global);

const MenuLoader = () => {
  return (
    <div className={cx(style["px-4"])}>
      <ul className={cx(style["skeleton-loader-area"], style["flex-column"], style["align-items-start"])}>
        <li className={cx(style["loader-item"], style["w-75"], style["my-2"])}></li>
        <li className={cx(style["loader-item"], style["w-100"], style["my-2"])}></li>
        <li className={cx(style["loader-item"], style["w-75"], style["my-2"])}></li>
      </ul>
    </div>
  );
};

const DefaultOptionComponent = props => {
  const { option, parentProps } = props;
  const { getOptionLabel } = parentProps;

  const itemRef = useRef(null);
  const [isItemEllipsis, setIsItemEllipsis] = useState(false);
  const [showItemTooltip, setShowItemTooltip] = useState(false);
  const toggleItemTooltip = () => setShowItemTooltip(prevState => !prevState);

  useEffect(() => {
    setIsItemEllipsis(itemRef.current?.scrollWidth > itemRef.current?.offsetWidth);
  }, []);

  return (
    <div className={cx(style["d-flex"], style["align-items-center"])}>
      <div ref={itemRef} className={style["text-truncate"]}>
        {getOptionLabel?.(option) ?? option?.label}
      </div>

      {isItemEllipsis && (
        <Tooltip
          placement="bottom"
          isOpen={showItemTooltip}
          toggle={toggleItemTooltip}
          target={itemRef.current}
          boundariesElement={document.getElementById("app")}>
          {option.name}
        </Tooltip>
      )}
    </div>
  );
};

const ReactstrapDropdown = ({
  loading,
  options = [],
  defaultValue = {},
  getOptionValue = null,
  getOptionLabel = null,
  isOptionDisabled = null,
  updateState = null,
  placeholderSearch = "",
  ...props
}) => {
  const randomSearchBoxId = `search-${getRandomString(8)}`;
  const valueRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [value, setValue] = useState(defaultValue);
  const [isValueEllipsis, setIsValueEllipsis] = useState(false);
  const [showValueTooltip, setShowValueTooltip] = useState(false);

  const CustomOptionComponent = props?.components?.Option;
  const OptionComponent = CustomOptionComponent ?? DefaultOptionComponent;

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);
  const toggleValueTooltip = () => setShowValueTooltip(prevState => !prevState);

  const searchHandler = e => {
    if (options) {
      const keyword = e.target.value.trim();
      const result = options.filter(option =>
        (getOptionLabel?.(option) ?? value?.label)?.toLowerCase().includes(keyword.toLowerCase())
      );
      setFilteredOptions(result);
    }
  };

  const selectItemHandler = option => {
    setValue(option);
    updateState?.(option);
  };

  useEffect(() => {
    setValue(defaultValue);
    setTimeout(() => {
      setIsValueEllipsis(valueRef.current?.scrollWidth > valueRef.current?.offsetWidth); // show tooltp if ellipsis
    }, 100);
  }, [defaultValue]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return (
    <UncontrolledDropdown isOpen={dropdownOpen} toggle={toggleDropdown} className={style["dropdown-with-search"]}>
      <DropdownToggle caret tag="a">
        <span className={cx(style["me-1"], style["text-truncate"])} ref={valueRef}>
          {getOptionLabel?.(value) ?? value?.label}

          {isValueEllipsis && (
            <Tooltip
              placement="bottom"
              isOpen={showValueTooltip}
              toggle={toggleValueTooltip}
              target={valueRef.current}
              boundariesElement={document.getElementById("app")}>
              {getOptionLabel?.(value) ?? value?.label}
            </Tooltip>
          )}
        </span>
        <Icon icon="ui-arrow-down" />
      </DropdownToggle>
      <DropdownMenu className={cx(style["shadow"], style["border-0"])}>
        <div className={cx(style["search"], style["mb-3"])}>
          <Input
            cssModule={style}
            returnType="inputGroup"
            addonType="prepend"
            type="search"
            id={props?.id ?? randomSearchBoxId}
            name={props?.name ?? randomSearchBoxId}
            onKeyDown={e => (!options || loading) && e.preventDefault()}
            onChange={e => searchHandler(e)}
            autoFocus={props?.autoFocus ?? false}
            placeholder={placeholderSearch}
          />
        </div>

        <div className={cx(style["search-list-wrap"], style["customScroll"], style["scroll-Y"])}>
          {loading && <MenuLoader />}

          {!loading && filteredOptions.length === 0 && (
            <div className={cx(style["px-4"])}>{props?.noOptionsMessage?.() ?? "No results found"}</div>
          )}

          <div className={cx(style["customScroll"], style["scroll-Y"])}>
            {!loading &&
              filteredOptions.length > 0 &&
              filteredOptions.map((option, _idx) => (
                <DropdownItem
                  tag="a"
                  key={_idx}
                  toggle={isOptionDisabled?.(option) ?? true}
                  disabled={isOptionDisabled?.(option) ?? false}
                  active={getOptionValue?.(option) === getOptionValue?.(value)}
                  className={cx({
                    [style["active"]]: getOptionValue?.(option) === getOptionValue?.(value),
                    [style["disabled"]]: isOptionDisabled?.(option) ?? false,
                  })}
                  onClick={() => selectItemHandler(option)}>
                  <OptionComponent
                    {...{
                      _idx,
                      option,
                      parentProps: {
                        ...props,
                        loading,
                        options,
                        defaultValue,
                        getOptionValue,
                        getOptionLabel,
                        isOptionDisabled,
                        updateState,
                      },
                    }}
                  />
                </DropdownItem>
              ))}
          </div>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

ReactstrapDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  updateState: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  noOptionsMessage: PropTypes.func,
  isOptionDisabled: PropTypes.func,
  defaultValue: PropTypes.object,
  placeholderSearch: PropTypes.string,
  loading: PropTypes.bool,
  components: PropTypes.objectOf(PropTypes.elementType),
  componentsProps: PropTypes.objectOf(PropTypes.object),
};

export default ReactstrapDropdown;
